import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { getProducts } from "../../../../store/product/useCases/getProducts/action";
import { selectProduct } from "../../../../store/product/repository/selector";
import { setProductError } from "../../../../store/product/repository/slice";
import { PRODUCTS_PER_PAGE } from "../../../../store/product/useCases/getProducts/worker";
import { deleteProduct } from "../../../../store/product/useCases/deleteProduct/action";
import { updateProduct } from "../../../../store/product/useCases/updateProduct/action";
import { tokens } from "../../../../locales/tokens";

type ProductTab = "ACTIVE" | "ARCHIVE";

export const useProductsList = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const scrollRef = useRef<HTMLDivElement>(null);

  const tabFromUrl = (searchParams.get("tab") as ProductTab) || "ACTIVE";
  const pageFromUrl = Number(searchParams.get("page")) || 1;

  const [activeTab, setActiveTab] = useState<ProductTab>(tabFromUrl);
  const [page, setPage] = useState(pageFromUrl);

  const [isUpdateClick, setIsUpdateClick] = useState(false);
  const [isDeleteClick, setIsDeleteClick] = useState(false);
  const [isRequestList, setIsRequestList] = useState(false);

  const { isProductLoading, productList, productCount, productError } =
    useSelector(selectProduct);

  const pageAmount = useMemo(() => {
    const count = productCount / PRODUCTS_PER_PAGE;
    return count > 1 ? Math.ceil(count) : 1;
  }, [productCount]);

  useEffect(() => {
    dispatch(
      getProducts({
        page,
        isDeleted: activeTab === "ACTIVE" ? false : true,
      }),
    );
    dispatch(setProductError(null));
  }, [dispatch, activeTab, page]);

  useEffect(() => {
    if (!isProductLoading && isUpdateClick) {
      if (productError) {
        toast.error(productError);
        dispatch(setProductError(null));
      } else {
        toast.success(t(tokens.shop.messages.updated));
        dispatch(
          getProducts({
            page,
            isDeleted: activeTab === "ACTIVE" ? false : true,
          }),
        );
      }
      setIsUpdateClick(false);
    }
  }, [
    isProductLoading,
    isUpdateClick,
    productError,
    dispatch,
    page,
    activeTab,
    t,
  ]);

  useEffect(() => {
    if (!isProductLoading && isDeleteClick) {
      if (productError) {
        toast.error(productError);
        dispatch(setProductError(null));
      } else {
        dispatch(
          getProducts({
            page,
            isDeleted: activeTab === "ACTIVE" ? false : true,
          }),
        );
        activeTab === "ACTIVE"
          ? toast.success(t(tokens.shop.messages.removed))
          : toast.success(t(tokens.shop.messages.removedHard));
      }
      setIsDeleteClick(false);
    }
  }, [
    isProductLoading,
    isDeleteClick,
    productError,
    dispatch,
    page,
    activeTab,
    t,
  ]);

  useEffect(() => {
    if (!isProductLoading && isRequestList) {
      if (productError) {
        toast.error(productError);
        dispatch(setProductError(null));
      }
      setIsRequestList(false);
    }
  }, [isProductLoading, isRequestList, productError, dispatch]);

  const onToggleTab = () => {
    const nextTab: ProductTab = activeTab === "ACTIVE" ? "ARCHIVE" : "ACTIVE";

    setIsRequestList(true);
    setActiveTab(nextTab);
    setPage(1);

    setSearchParams({
      tab: nextTab,
      page: "1",
    });
  };

  const onChangePage = (_: unknown, selectedPage: number) => {
    setIsRequestList(true);
    setPage(selectedPage);

    setSearchParams({
      tab: activeTab,
      page: String(selectedPage),
    });
  };

  const onChangeProductVisibility = (productId: number, isHidden: boolean) => {
    if (!productId) {
      toast.error("Something went wrong");
      return;
    }

    setIsUpdateClick(true);
    dispatch(updateProduct({ product_id: productId, is_hidden: isHidden }));
  };

  const onDeleteProduct = (productId: number, mode?: string) => {
    if (!productId) return;

    setIsDeleteClick(true);
    dispatch(deleteProduct(mode ? { id: productId, mode } : { id: productId }));
  };

  return {
    activeTab,
    isRequestList,
    productList,
    page,
    pageAmount,
    isProductLoading,
    onChangePage,
    onToggleTab,
    onDeleteProduct,
    onChangeProductVisibility,
    scrollRef,
  };
};
